import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ChatBubbleLeftRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useSendMessageMutation } from '../../store/api/localApi';
import { MessageType } from '../../types';
import { useToast } from '../../components/Toast';

interface MessageParentsButtonProps {
  studentId: string;
  studentName: string;
}

const MessageParentsButton: React.FC<MessageParentsButtonProps> = ({ studentId, studentName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [repliesAllowed, setRepliesAllowed] = useState(true);

  const { showSuccess, showError } = useToast();
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();

  const handleOpen = () => setIsOpen(true);

  const handleClose = () => {
    if (isSending) return;
    setIsOpen(false);
    setTitle('');
    setBody('');
    setRepliesAllowed(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      showError('Title is required.');
      return;
    }
    if (!body.trim()) {
      showError('Message body is required.');
      return;
    }

    try {
      const result = await sendMessage({
        type: MessageType.PARENT_DM,
        studentId,
        title: title.trim(),
        body: body.trim(),
        repliesAllowed,
      }).unwrap();

      const count = result.recipientCount;
      if (count === 0) {
        showError('No parents are linked to this student yet.');
      } else {
        showSuccess(`Sent to ${count} parent${count === 1 ? '' : 's'}.`);
      }
      handleClose();
    } catch (err: unknown) {
      const data =
        err && typeof err === 'object' && 'data' in err
          ? (err as { data: { error?: string; message?: string } | undefined }).data
          : undefined;
      showError(data?.error || data?.message || 'Failed to send message. Please try again.');
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="student-message-parents-btn"
        onClick={handleOpen}
        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
      >
        <ChatBubbleLeftRightIcon className="-ml-0.5 mr-2 h-4 w-4" />
        Message parents
      </button>

      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={handleClose}
          data-testid="student-message-parents-modal"
        >
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  {/* Close button */}
                  <div className="absolute right-0 top-0 pr-4 pt-4">
                    <button
                      type="button"
                      data-testid="student-message-parents-close"
                      onClick={handleClose}
                      disabled={isSending}
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 disabled:opacity-50"
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold leading-6 text-gray-900"
                      >
                        Message parents of {studentName}
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-gray-500">
                        This message will be sent as a direct message to the parents linked to this
                        student.
                      </p>

                      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                        <div>
                          <label
                            htmlFor="message-parents-title"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Title <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="message-parents-title"
                            type="text"
                            data-testid="student-message-parents-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g. Upcoming school trip"
                            disabled={isSending}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"
                          />
                        </div>

                        <div>
                          <label
                            htmlFor="message-parents-body"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Message <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            id="message-parents-body"
                            data-testid="student-message-parents-body"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            placeholder="Write your message here…"
                            rows={5}
                            disabled={isSending}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"
                          />
                        </div>

                        <label className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            data-testid="compose-replies-allowed"
                            checked={repliesAllowed}
                            onChange={(e) => setRepliesAllowed(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          Parents can reply
                        </label>

                        <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse gap-3">
                          <button
                            type="submit"
                            data-testid="student-message-parents-submit"
                            disabled={isSending || !title.trim() || !body.trim()}
                            className="inline-flex w-full justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
                          >
                            {isSending ? 'Sending…' : 'Send message'}
                          </button>
                          <button
                            type="button"
                            data-testid="student-message-parents-cancel"
                            onClick={handleClose}
                            disabled={isSending}
                            className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 sm:mt-0 sm:w-auto"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default MessageParentsButton;
